/**
 * Lot Status Enum
 *
 * Defines valid lot statuses as returned by the site map API (normalized).
 * Prevents magic strings and ensures type consistency across the feature.
 * Uses singleton pattern for instance management.
 *
 * @module nativeInteractiveSiteMap/enums/lotStatus
 */

export default class LotStatus {
  static instances = {};

  /**
   * Lot is available for purchase.
   */
  static get Available() {
    return this.getInstance('available');
  }

  /**
   * Lot is coming soon — not yet released. API returns "ComingSoon" (C# enum).
   */
  static get ComingSoon() {
    return this.getInstance('coming-soon');
  }

  /**
   * Lot has been sold.
   */
  static get Sold() {
    return this.getInstance('sold');
  }

  /**
   * Lot is not available, or the API returned NotFound/an unrecognized status.
   */
  static get Unavailable() {
    return this.getInstance('unavailable');
  }

  /**
   * Get lot status instance from a normalized API key (lowercase PascalCase).
   * @param {string} key - Lowercased API value (e.g. 'available', 'comingsoon', 'sold')
   * @returns {LotStatus|null} Lot status instance or null if key not recognized
   */
  static fromKey(key) {
    const map = {
      'available':   LotStatus.Available,
      'comingsoon':  LotStatus.ComingSoon,
      'sold':        LotStatus.Sold,
      'unavailable': LotStatus.Unavailable,
    };
    return map[key] ?? null;
  }

  /**
   * @private
   */
  static getInstance(key) {
    if (!this.instances[key]) {
      this.instances[key] = new LotStatus(key);
    }
    return this.instances[key];
  }

  constructor(name) {
    this.name = name;
  }

  toString() {
    return this.name;
  }
}
